import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Trash2, ArrowUp, ArrowDown, X, CheckCircle2, CalendarDays, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEDBACK_LABELS = {
  aide: "Ça m'a aidée",
  peu: "Peu de changement",
  pas_adapte: "Pas adapté aujourd'hui",
};

// Module Super Admin « Mon accompagnement » : attribuer des contenus RAV à une
// patiente suivie au cabinet/en visio, suivre ses validations entre les RDV.
export default function Accompagnements() {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null); // accompagnement ouvert (édition + validations)
  const [creating, setCreating] = useState(false);
  const token = localStorage.getItem("rav-token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    axios.get(`${API}/admin/accompaniments`, { headers }).then(({ data }) => setList(data.accompaniments || [])).catch(() => {});
  };
  useEffect(load, []);

  const openDetail = (aid) => {
    axios.get(`${API}/admin/accompaniments/${aid}`, { headers }).then(({ data }) => setDetail(data.accompaniment)).catch(() => toast.error("Ouverture impossible."));
  };

  const close = async (aid) => {
    await axios.post(`${API}/admin/accompaniments/${aid}/close`, {}, { headers }).catch(() => {});
    toast.success("Accompagnement clôturé.");
    setDetail(null);
    load();
  };

  if (detail) return <AccompagnementDetail accomp={detail} headers={headers} onBack={() => { setDetail(null); load(); }} onClose={() => close(detail.id)} />;
  if (creating) return <AccompagnementForm headers={headers} onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />;

  return (
    <div data-testid="admin-accompagnements">
      <h1 className="rav-admin-title">Mon accompagnement</h1>
      <p className="rav-page-sub rav-admin-note">
        Attribuez des pratiques RAV à une patiente suivie au cabinet ou en visio. Elle les retrouve dans son application, même sans abonnement.
      </p>
      <div className="rav-session-actions" style={{ marginBottom: 16 }}>
        <button type="button" className="rav-primary-button rav-session-btn" data-testid="accomp-create-button" onClick={() => setCreating(true)}>
          <Plus size={15} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 6 }} />
          Nouvel accompagnement
        </button>
      </div>
      {list.length === 0 ? (
        <p className="rav-admin-note" data-testid="accomp-empty">Aucun accompagnement pour le moment.</p>
      ) : (
        <div className="rav-admin-list">
          {list.map((a) => (
            <div key={a.id} className="rav-admin-row" data-testid={`accomp-row-${a.id}`}>
              <span className="rav-admin-row-label">{a.user_name || a.user_email}</span>
              <span className="rav-admin-row-sub">
                {a.status === "actif" ? "Actif" : "Clôturé"} · {a.contents_count} contenu{a.contents_count > 1 ? "s" : ""} · {a.validations_count} validation{a.validations_count > 1 ? "s" : ""}
                {a.next_appointment ? ` · RDV : ${a.next_appointment}` : ""}
              </span>
              <span className="rav-admin-row-actions">
                <button type="button" className="rav-text-link" data-testid={`accomp-open-${a.id}`} onClick={() => openDetail(a.id)}>
                  Ouvrir
                </button>
                {a.status === "actif" && (
                  <button type="button" className="rav-text-link" data-testid={`accomp-close-${a.id}`} onClick={() => close(a.id)}>
                    Clôturer
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ----- Formulaire de création -----
function AccompagnementForm({ headers, onDone, onCancel }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [chosenUser, setChosenUser] = useState(null);
  const [allContents, setAllContents] = useState([]);
  const [selected, setSelected] = useState([]); // ids ordonnés
  const [note, setNote] = useState("");
  const [nextRdv, setNextRdv] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [contentQuery, setContentQuery] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/contents`, { headers }).then(({ data }) => setAllContents((data.contents || []).filter((c) => c.status !== "archivé"))).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      axios.get(`${API}/admin/search-users?q=${encodeURIComponent(query)}`, { headers }).then(({ data }) => setResults(data.users || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const addContent = (cid) => setSelected((s) => (s.includes(cid) ? s : [...s, cid]));
  const removeContent = (cid) => setSelected((s) => s.filter((x) => x !== cid));
  const move = (i, dir) => {
    setSelected((s) => {
      const next = [...s];
      const j = i + dir;
      if (j < 0 || j >= next.length) return s;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async () => {
    if (!chosenUser) return toast.error("Choisissez une utilisatrice.");
    if (selected.length === 0) return toast.error("Sélectionnez au moins un contenu.");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/accompaniments`, {
        user_id: chosenUser.id,
        content_ids: selected,
        note: note.trim(),
        next_appointment: nextRdv.trim() || null,
        end_date: endDate || null,
      }, { headers });
      toast.success("Accompagnement créé — visible dans l'application de votre patiente.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création impossible.");
    }
    setSaving(false);
  };

  const byId = Object.fromEntries(allContents.map((c) => [c.id, c]));

  return (
    <div data-testid="accomp-form">
      <h1 className="rav-admin-title">Nouvel accompagnement</h1>

      <h2 className="rav-admin-h2">1. La patiente</h2>
      {chosenUser ? (
        <div className="rav-admin-row" data-testid="accomp-chosen-user">
          <span className="rav-admin-row-label">{chosenUser.name} {chosenUser.last_name}</span>
          <span className="rav-admin-row-sub">{chosenUser.email}</span>
          <span className="rav-admin-row-actions">
            <button type="button" className="rav-text-link" data-testid="accomp-change-user" onClick={() => setChosenUser(null)}>Changer</button>
          </span>
        </div>
      ) : (
        <>
          <label className="rav-field">
            <span><Search size={13} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 4 }} />Rechercher par nom ou e-mail</span>
            <input className="rav-input" data-testid="accomp-user-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ex. camille@…" />
          </label>
          {results.map((u) => (
            <button key={u.id} type="button" className="rav-notif-item" data-testid={`accomp-user-${u.id}`} onClick={() => setChosenUser(u)} style={{ width: "100%", textAlign: "left" }}>
              <span className="rav-notif-item-name">{u.name} {u.last_name}</span>
              <span className="rav-notif-item-text">{u.email}</span>
            </button>
          ))}
        </>
      )}

      <h2 className="rav-admin-h2">2. Les contenus, dans l'ordre</h2>
      <label className="rav-field">
        <span><Search size={13} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 4 }} />Retrouver un contenu par son nom</span>
        <input className="rav-input" data-testid="accomp-content-search" value={contentQuery} onChange={(e) => setContentQuery(e.target.value)} placeholder="Ex. Souffle du matin…" />
      </label>
      {selected.length > 0 && (
        <div className="rav-admin-list" style={{ marginBottom: 12 }}>
          {selected.map((cid, i) => (
            <div key={cid} className="rav-admin-row" data-testid={`accomp-selected-${i}`}>
              <span className="rav-admin-row-label">{i + 1}. {byId[cid]?.title || cid}</span>
              <span className="rav-admin-row-sub">{byId[cid]?.kind}</span>
              <span className="rav-admin-row-actions">
                <button type="button" className="rav-text-link" data-testid={`accomp-up-${i}`} onClick={() => move(i, -1)} aria-label="Monter"><ArrowUp size={13} /></button>
                <button type="button" className="rav-text-link" data-testid={`accomp-down-${i}`} onClick={() => move(i, 1)} aria-label="Descendre"><ArrowDown size={13} /></button>
                <button type="button" className="rav-text-link" data-testid={`accomp-remove-${i}`} onClick={() => removeContent(cid)} aria-label="Retirer"><X size={13} /></button>
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="rav-admin-list">
        {allContents.filter((c) => !selected.includes(c.id)).filter((c) => !contentQuery.trim() || c.title.toLowerCase().includes(contentQuery.trim().toLowerCase())).map((c) => (
          <div key={c.id} className="rav-admin-row" data-testid={`accomp-content-${c.id}`}>
            <span className="rav-admin-row-label">{c.title}</span>
            <span className="rav-admin-row-sub">{c.kind} · {c.minutes} min</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`accomp-add-${c.id}`} onClick={() => addContent(c.id)}>
                <Plus size={13} strokeWidth={1.8} style={{ verticalAlign: "-2px" }} /> Ajouter
              </button>
            </span>
          </div>
        ))}
      </div>

      <h2 className="rav-admin-h2">3. Votre consigne (facultatif)</h2>
      <label className="rav-field">
        <span>Intention ou consigne pour votre patiente</span>
        <textarea className="rav-input" data-testid="accomp-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex. Pratiquez en douceur, à votre rythme, avant le coucher." />
      </label>

      <h2 className="rav-admin-h2">4. Dates (facultatif)</h2>
      <label className="rav-field">
        <span><CalendarDays size={13} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 4 }} />Prochain rendez-vous</span>
        <input className="rav-input" data-testid="accomp-next-rdv" value={nextRdv} onChange={(e) => setNextRdv(e.target.value)} placeholder="Ex. 15 octobre 2026 à 14h" />
      </label>
      <label className="rav-field">
        <span>Date de fin de l'accompagnement</span>
        <input className="rav-input" data-testid="accomp-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </label>

      <div className="rav-session-actions">
        <button type="button" className="rav-primary-button rav-session-btn" data-testid="accomp-save" onClick={save} disabled={saving}>
          {saving ? "Création…" : "Créer l'accompagnement"}
        </button>
        <button type="button" className="rav-secondary-button rav-session-btn" data-testid="accomp-cancel" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

// ----- Détail : contenus, édition, validations de la patiente -----
function AccompagnementDetail({ accomp, headers, onBack, onClose }) {
  const [editing, setEditing] = useState(false);
  const vals = accomp.my_validations_map || Object.fromEntries((accomp.validations || []).map((v) => [v.content_id, v]));

  return (
    <div data-testid="accomp-detail">
      <button type="button" className="rav-text-link" data-testid="accomp-back" onClick={onBack}>← Tous les accompagnements</button>
      <h1 className="rav-admin-title">{accomp.user_name} <span style={{ fontWeight: 400, fontSize: "0.7em" }}>({accomp.user_email})</span></h1>
      <p className="rav-page-sub rav-admin-note">
        {accomp.status === "actif" ? "Actif" : "Clôturé"}
        {accomp.next_appointment ? ` · Prochain RDV : ${accomp.next_appointment}` : ""}
        {accomp.end_date ? ` · Fin prévue : ${accomp.end_date}` : ""}
      </p>
      {accomp.note && <p className="rav-admin-note" data-testid="accomp-detail-note">Consigne : « {accomp.note} »</p>}

      <h2 className="rav-admin-h2">Contenus attribués et validations</h2>
      <div className="rav-admin-list">
        {(accomp.contents || []).map((c, i) => {
          const v = vals[c.id];
          return (
            <div key={c.id} className="rav-admin-row" data-testid={`accomp-detail-content-${i}`}>
              <span className="rav-admin-row-label">{i + 1}. {c.title}</span>
              <span className="rav-admin-row-sub">
                {c.kind} · {c.minutes} min
                {v ? ` · Fait le ${new Date(v.done_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}${v.feedback ? ` — ${FEEDBACK_LABELS[v.feedback]}` : ""}` : " · Pas encore fait"}
              </span>
              <span className="rav-admin-row-actions">
                {v ? <CheckCircle2 size={15} strokeWidth={1.8} color="#7a9b6d" data-testid={`accomp-done-icon-${i}`} /> : null}
              </span>
            </div>
          );
        })}
      </div>

      <div className="rav-session-actions">
        {accomp.status === "actif" && (
          <>
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="accomp-edit" onClick={() => setEditing(true)}>Modifier les contenus</button>
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="accomp-close-detail" onClick={onClose}>
              <Lock size={14} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 4 }} />Clôturer
            </button>
          </>
        )}
      </div>

      {editing && (
        <AccompagnementEdit accomp={accomp} headers={headers} onDone={() => { setEditing(false); onBack(); }} onCancel={() => setEditing(false)} />
      )}
    </div>
  );
}

// ----- Édition : reprend le formulaire avec les valeurs existantes -----
function AccompagnementEdit({ accomp, headers, onDone, onCancel }) {
  const [allContents, setAllContents] = useState([]);
  const [selected, setSelected] = useState(accomp.content_ids || []);
  const [note, setNote] = useState(accomp.note || "");
  const [nextRdv, setNextRdv] = useState(accomp.next_appointment || "");
  const [endDate, setEndDate] = useState(accomp.end_date || "");
  const [saving, setSaving] = useState(false);
  const [contentQuery, setContentQuery] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/contents`, { headers }).then(({ data }) => setAllContents((data.contents || []).filter((c) => c.status !== "archivé"))).catch(() => {});
  }, []);

  const addContent = (cid) => setSelected((s) => (s.includes(cid) ? s : [...s, cid]));
  const removeContent = (cid) => setSelected((s) => s.filter((x) => x !== cid));
  const move = (i, dir) => setSelected((s) => {
    const next = [...s];
    const j = i + dir;
    if (j < 0 || j >= next.length) return s;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/accompaniments/${accomp.id}`, {
        user_id: accomp.user_id,
        title: accomp.title,
        content_ids: selected,
        note: note.trim(),
        next_appointment: nextRdv.trim() || null,
        end_date: endDate || null,
      }, { headers });
      toast.success("Accompagnement mis à jour.");
      onDone();
    } catch {
      toast.error("Mise à jour impossible.");
    }
    setSaving(false);
  };

  const byId = Object.fromEntries(allContents.map((c) => [c.id, c]));

  return (
    <div data-testid="accomp-edit-form" style={{ marginTop: 20 }}>
      <h2 className="rav-admin-h2">Modifier</h2>
      {selected.map((cid, i) => (
        <div key={cid} className="rav-admin-row" data-testid={`accomp-edit-selected-${i}`}>
          <span className="rav-admin-row-label">{i + 1}. {byId[cid]?.title || accomp.contents?.find((c) => c.id === cid)?.title || cid}</span>
          <span className="rav-admin-row-actions">
            <button type="button" className="rav-text-link" data-testid={`accomp-edit-up-${i}`} onClick={() => move(i, -1)}><ArrowUp size={13} /></button>
            <button type="button" className="rav-text-link" data-testid={`accomp-edit-down-${i}`} onClick={() => move(i, 1)}><ArrowDown size={13} /></button>
            <button type="button" className="rav-text-link" data-testid={`accomp-edit-remove-${i}`} onClick={() => removeContent(cid)}><Trash2 size={13} /></button>
          </span>
        </div>
      ))}
      <label className="rav-field">
        <span><Search size={13} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 4 }} />Retrouver un contenu par son nom</span>
        <input className="rav-input" data-testid="accomp-edit-content-search" value={contentQuery} onChange={(e) => setContentQuery(e.target.value)} placeholder="Ex. Souffle du matin…" />
      </label>
      <div className="rav-admin-list">
        {allContents.filter((c) => !selected.includes(c.id)).filter((c) => !contentQuery.trim() || c.title.toLowerCase().includes(contentQuery.trim().toLowerCase())).map((c) => (
          <div key={c.id} className="rav-admin-row">
            <span className="rav-admin-row-label">{c.title}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`accomp-edit-add-${c.id}`} onClick={() => addContent(c.id)}><Plus size={13} /> Ajouter</button>
            </span>
          </div>
        ))}
      </div>
      <label className="rav-field">
        <span>Consigne</span>
        <textarea className="rav-input" data-testid="accomp-edit-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <label className="rav-field">
        <span>Prochain rendez-vous</span>
        <input className="rav-input" data-testid="accomp-edit-rdv" value={nextRdv} onChange={(e) => setNextRdv(e.target.value)} />
      </label>
      <label className="rav-field">
        <span>Date de fin</span>
        <input className="rav-input" data-testid="accomp-edit-end" type="date" value={endDate || ""} onChange={(e) => setEndDate(e.target.value)} />
      </label>
      <div className="rav-session-actions">
        <button type="button" className="rav-primary-button rav-session-btn" data-testid="accomp-edit-save" onClick={save} disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
        <button type="button" className="rav-secondary-button rav-session-btn" data-testid="accomp-edit-cancel" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}
